import { useEffect, useState } from "react";
import { CalendarOutlined, EyeOutlined, InboxOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Button, Empty, List, Modal, Skeleton, Space, Typography } from "antd";
import dayjs from "dayjs";

import { RequestStatus, traineeProgramActions } from "@fitness-app/app-store";
import { type ClientTrainingProgram } from "@fitness-app/data-models/entities/TrainingProgram";

import ArchivedProgramSchedule from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeTrainingProgram/ArchivedProgramSchedule";
import { useAppDispatch, useAppSelector } from "~/store/initializeStore";

interface TraineeArchivedProgramsProps {
  traineeId: string;
}

const formatDate = (value?: string | null) => (value ? dayjs(value, "YYYY-MM-DD").format("DD.MM.YYYY") : "-");

/**
 * TRE-1833 — historia planów treningowych klientki w panelu trenerki.
 *
 * Rozpiska konkretnego planu leci osobnym zapytaniem (`fetchOnceProgramWithDetails`) dopiero
 * po kliknięciu: `client_training_program_details.workouts` to spory jsonb, a klientka
 * potrafi mieć 80 planów archiwalnych.
 */
export const TraineeArchivedPrograms = ({ traineeId }: TraineeArchivedProgramsProps) => {
  const dispatch = useAppDispatch();
  const { archivedPrograms, archivedProgramsStatus, archivedProgramsHasMore } = useAppSelector(
    (store) => store.traineeProgram,
  );
  const [selectedProgram, setSelectedProgram] = useState<ClientTrainingProgram | null>(null);

  useEffect(() => {
    if (traineeId) {
      void dispatch(traineeProgramActions.fetchArchivedPrograms({ traineeId, offset: 0 }));
    }
  }, [dispatch, traineeId]);

  const { data: selectedProgramDetails, isLoading: isLoadingDetails } = useQuery(
    ["archivedProgramDetails", selectedProgram?.id],
    {
      enabled: Boolean(selectedProgram?.id),
      staleTime: 60 * 5000,
      queryFn: async () => {
        if (!selectedProgram) {
          return null;
        }

        const { details } = await dispatch(
          traineeProgramActions.fetchOnceProgramWithDetails({ id: selectedProgram.id }),
        ).unwrap();

        return details;
      },
    },
  );

  const isFirstLoad = archivedProgramsStatus === RequestStatus.FETCHING && !archivedPrograms.length;

  if (isFirstLoad) {
    return <Skeleton active paragraph={{ rows: 6 }} />;
  }

  if (!archivedPrograms.length) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="Ta klientka nie ma jeszcze archiwalnych planów treningowych"
      />
    );
  }

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Typography.Title level={5}>Poprzednie plany treningowe:</Typography.Title>

      <List
        itemLayout="horizontal"
        bordered
        dataSource={archivedPrograms}
        renderItem={(program) => (
          <List.Item
            actions={[
              <Button
                key="preview"
                type="link"
                icon={<EyeOutlined />}
                onClick={() => setSelectedProgram(program)}
                // Plan bez harmonogramu nie ma czego pokazać — rozpiska wisi na
                // `workoutsSchedule`, nie na samych treningach.
                disabled={!program.workoutsSchedule}
              >
                podgląd
              </Button>,
            ]}
          >
            <List.Item.Meta
              title={<Typography.Text strong>{program.name}</Typography.Text>}
              description={
                <Space direction="horizontal" size={16} wrap>
                  <Space direction="horizontal" size={8}>
                    <CalendarOutlined />
                    <Typography.Text type="secondary">
                      Plan od {formatDate(program.startDate)} do {formatDate(program.endDate)}
                    </Typography.Text>
                  </Space>
                  {program.archivedAt ? (
                    <Space direction="horizontal" size={8}>
                      <InboxOutlined />
                      <Typography.Text type="secondary">
                        Zarchiwizowany {dayjs(program.archivedAt).format("DD.MM.YYYY")}
                      </Typography.Text>
                    </Space>
                  ) : null}
                </Space>
              }
            />
          </List.Item>
        )}
      />

      {archivedProgramsHasMore ? (
        <div className="flex items-center justify-center">
          <Button
            loading={archivedProgramsStatus === RequestStatus.FETCHING}
            onClick={() =>
              void dispatch(traineeProgramActions.fetchArchivedPrograms({ traineeId, offset: archivedPrograms.length }))
            }
          >
            Pokaż starsze plany
          </Button>
        </div>
      ) : null}

      {/*
       * Zwykły `Modal`, nie `ModalForm` — ten drugi dokłada stopkę z „Zapisz" i wstrzykuje
       * `formController`. Tutaj nie ma czego zapisywać.
       */}
      <Modal
        open={Boolean(selectedProgram)}
        width={1100}
        footer={null}
        destroyOnHidden
        title={
          selectedProgram
            ? `${selectedProgram.name} (${formatDate(selectedProgram.startDate)} – ${formatDate(selectedProgram.endDate)})`
            : null
        }
        onCancel={() => setSelectedProgram(null)}
      >
        {isLoadingDetails || !selectedProgram ? (
          <Skeleton active paragraph={{ rows: 8 }} />
        ) : (
          <ArchivedProgramSchedule program={selectedProgram} details={selectedProgramDetails ?? null} />
        )}
      </Modal>
    </Space>
  );
};

export default TraineeArchivedPrograms;
